const QUEUE_STORAGE_KEY = 'rastyle_playback_queue_v2';

const initialPlaybackQueue = Object.freeze({
  items: [],
  currentIndex: -1,
  history: [],
  source: null,
  shuffle: false,
  repeatMode: 'off',
  autoplay: true,
  updatedAt: null,
});

const queueItemId = (track = {}) => String(
  track.track_id
  || track.id
  || track.youtube_id
  || track.youtubeId
  || track.spotify_id
  || track.embed_url
  || track.video_url
  || track.url
  || track.externalUrl
  || '',
);

const normalizeQueueItem = (track = {}) => {
  const youtubeId = track.youtube_id || track.youtubeId || track.video_id || '';
  const isYouTube = track.track_type === 'youtube' || track.type === 'youtube' || Boolean(youtubeId);
  return {
    track_id: queueItemId(track),
    track_type: isYouTube
      ? 'youtube'
      : (track.track_type || track.type || (track.synced_from_hearthis || track.hearthis_url ? 'mixtape' : 'release')),
    title: track.title || track.name || '',
    artist: track.artist || track.dj || track.channel_title || '',
    cover: track.cover || track.image || track.thumbnail || '',
    duration: Number(track.duration) || 0,
    hearthis_url: track.hearthis_url || '',
    stream_url: track.stream_url || '',
    audio_url: track.audio_url || '',
    resolved_audio_url: track.resolved_audio_url || '',
    video_url: track.video_url || '',
    embed_url: track.embed_url || track.embedUrl || '',
    provider: track.provider || '',
    synced_from_hearthis: Boolean(track.synced_from_hearthis),
    spotify_id: track.spotify_id || '',
    youtube_id: youtubeId,
    url: track.url || track.externalUrl || '',
    externalUrl: track.externalUrl || track.external_url || track.url || '',
    video_type: track.video_type || track.videoType || '',
    genre: track.genre || '',
    mood: track.mood || '',
    era: track.era || '',
    tags: Array.isArray(track.tags) ? track.tags : [],
    collection_id: track.collection_id || track.mixtape_id || track.album_id || '',
    collection_title: track.collection_title || track.album || '',
    featured: Boolean(track.featured),
    creator_id: track.creator_id || track.creatorId || '',
    creator_slug: track.creator_slug || track.creatorSlug || '',
    playback_surface: track.playback_surface || track.playbackSurface || '',
    autoplay_added: Boolean(track.autoplay_added),
    recommendation_reason: track.recommendation_reason || track.reason || '',
    recommendation_source: track.recommendation_source || track.source || '',
    recommendation_score: Number(track.recommendation_score ?? track.score) || 0,
  };
};

const uniqueItems = (items = []) => {
  const seen = new Set();
  return items.map(normalizeQueueItem).filter((item) => {
    if (!item.track_id || seen.has(item.track_id)) return false;
    seen.add(item.track_id);
    return true;
  });
};

const clampIndex = (index, length) => {
  const value = Number(index);
  if (!Number.isInteger(value) || value < 0 || value >= length) return -1;
  return value;
};

const stamp = (state, now = Date.now()) => ({ ...state, updatedAt: new Date(now).toISOString() });

const getAdvanceIndex = (state, random = Math.random) => {
  if (!state.items.length) return -1;
  if (state.repeatMode === 'one' && state.currentIndex >= 0) return state.currentIndex;
  if (state.shuffle && state.items.length > 1) {
    const candidates = state.items.map((_, index) => index).filter((index) => index !== state.currentIndex);
    return candidates[Math.floor(random() * candidates.length)];
  }
  const nextIndex = state.currentIndex + 1;
  if (nextIndex < state.items.length) return nextIndex;
  if (state.repeatMode === 'all') return 0;
  return -1;
};

const playbackQueueReducer = (state, action) => {
  switch (action.type) {
    case 'RESTORE': {
      const items = uniqueItems(action.payload?.items);
      return stamp({
        ...initialPlaybackQueue,
        ...action.payload,
        items,
        currentIndex: clampIndex(action.payload?.currentIndex, items.length),
        history: Array.isArray(action.payload?.history) ? action.payload.history : [],
        repeatMode: ['off', 'all', 'one'].includes(action.payload?.repeatMode) ? action.payload.repeatMode : 'off',
        shuffle: Boolean(action.payload?.shuffle),
        autoplay: action.payload?.autoplay !== false,
      }, action.payload?.now);
    }
    case 'REPLACE': {
      const items = uniqueItems(action.payload?.items);
      return stamp({
        ...state,
        items,
        currentIndex: clampIndex(action.payload?.currentIndex ?? 0, items.length),
        history: [],
        source: action.payload?.source || null,
      }, action.payload?.now);
    }
    case 'ADD': {
      const item = normalizeQueueItem(action.payload?.track);
      if (!item.track_id || state.items.some((entry) => entry.track_id === item.track_id)) return state;
      return stamp({ ...state, items: [...state.items, item] }, action.payload?.now);
    }
    case 'ADD_MANY': {
      const incoming = uniqueItems(action.payload?.tracks);
      const existing = new Set(state.items.map((item) => item.track_id));
      const additions = incoming.filter((item) => !existing.has(item.track_id));
      return additions.length ? stamp({ ...state, items: [...state.items, ...additions] }, action.payload?.now) : state;
    }
    case 'PLAY_NEXT': {
      const item = normalizeQueueItem(action.payload?.track);
      if (!item.track_id) return state;
      const currentId = state.items[state.currentIndex]?.track_id;
      const filtered = state.items.filter((entry) => entry.track_id !== item.track_id);
      const currentIndex = currentId ? filtered.findIndex((entry) => entry.track_id === currentId) : -1;
      const insertAt = currentIndex >= 0 ? currentIndex + 1 : 0;
      const items = [...filtered.slice(0, insertAt), item, ...filtered.slice(insertAt)];
      return stamp({ ...state, items, currentIndex }, action.payload?.now);
    }
    case 'REMOVE': {
      const index = state.items.findIndex((item) => item.track_id === String(action.payload?.trackId));
      if (index < 0) return state;
      const items = state.items.filter((_, itemIndex) => itemIndex !== index);
      let currentIndex = state.currentIndex;
      if (index < currentIndex) currentIndex -= 1;
      else if (index === currentIndex) currentIndex = -1;
      return stamp({ ...state, items, currentIndex }, action.payload?.now);
    }
    case 'REORDER': {
      const from = Number(action.payload?.fromIndex);
      const to = Number(action.payload?.toIndex);
      if (!Number.isInteger(from) || !Number.isInteger(to) || from < 0 || to < 0 || from >= state.items.length || to >= state.items.length || from === to) return state;
      const items = [...state.items];
      const [moved] = items.splice(from, 1);
      items.splice(to, 0, moved);
      let currentIndex = state.currentIndex;
      if (currentIndex === from) currentIndex = to;
      else if (from < currentIndex && to >= currentIndex) currentIndex -= 1;
      else if (from > currentIndex && to <= currentIndex) currentIndex += 1;
      return stamp({ ...state, items, currentIndex }, action.payload?.now);
    }
    case 'SET_CURRENT': {
      const currentIndex = clampIndex(action.payload?.index, state.items.length);
      if (currentIndex === state.currentIndex) return state;
      const previous = state.items[state.currentIndex];
      const history = previous ? [...state.history, previous.track_id].slice(-50) : state.history;
      return stamp({ ...state, currentIndex, history }, action.payload?.now);
    }
    case 'ADVANCE': {
      const nextIndex = Number.isInteger(action.payload?.index)
        ? clampIndex(action.payload.index, state.items.length)
        : getAdvanceIndex(state, action.payload?.random);
      if (nextIndex < 0) return state;
      const previous = state.items[state.currentIndex];
      return stamp({
        ...state,
        currentIndex: nextIndex,
        history: previous ? [...state.history, previous.track_id].slice(-50) : state.history,
      }, action.payload?.now);
    }
    case 'TOGGLE_SHUFFLE':
      return stamp({ ...state, shuffle: !state.shuffle }, action.payload?.now);
    case 'CYCLE_REPEAT': {
      const repeatMode = state.repeatMode === 'off' ? 'all' : state.repeatMode === 'all' ? 'one' : 'off';
      return stamp({ ...state, repeatMode }, action.payload?.now);
    }
    case 'SET_AUTOPLAY':
      return stamp({ ...state, autoplay: Boolean(action.payload?.enabled) }, action.payload?.now);
    case 'CLEAR':
      return { ...initialPlaybackQueue };
    default:
      return state;
  }
};

const serializePlaybackQueue = (state) => JSON.stringify({
  items: state.items,
  currentIndex: state.currentIndex,
  history: state.history,
  source: state.source,
  shuffle: state.shuffle,
  repeatMode: state.repeatMode,
  autoplay: state.autoplay,
  updatedAt: state.updatedAt,
});

const restorePlaybackQueue = (storage = typeof localStorage === 'undefined' ? null : localStorage) => {
  try {
    const raw = storage?.getItem(QUEUE_STORAGE_KEY);
    if (!raw) return { ...initialPlaybackQueue };
    return playbackQueueReducer(initialPlaybackQueue, { type: 'RESTORE', payload: JSON.parse(raw) });
  } catch {
    return { ...initialPlaybackQueue };
  }
};

const persistPlaybackQueue = (state, storage = typeof localStorage === 'undefined' ? null : localStorage) => {
  try {
    storage?.setItem(QUEUE_STORAGE_KEY, serializePlaybackQueue(state));
    return true;
  } catch {
    return false;
  }
};

export {
  QUEUE_STORAGE_KEY,
  getAdvanceIndex,
  initialPlaybackQueue,
  normalizeQueueItem,
  playbackQueueReducer,
  persistPlaybackQueue,
  queueItemId,
  restorePlaybackQueue,
  serializePlaybackQueue,
  uniqueItems,
};
